import React, { useEffect } from 'react';
import { useState } from 'react';
import { Modal } from 'components/organisms/Modal';
import { Button, Loader } from 'legacy-components/componets';
import { useStripeOnboarding } from 'legacy-pages/landlord/profile/context/StripeOnboardingProvider';
import { useWindowSize } from 'hooks';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  renterName: string;
};

export const PayoutOnboardingInfoDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  renterName,
}) => {
  const { isMobile } = useWindowSize();
  const [isConnectedAccountCreationLoading, setIsConnectedAccountCreationLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { 
    OnboardingComponent, 
    handleCreateConnectedAccount, 
    connectedAccountId,
    isConnectedAccountOnboarded,
    stripeConnectInstance
  } = useStripeOnboarding();

  useEffect(() => {
    if (isConnectedAccountOnboarded) {
      onClose();
    }
  }, [isConnectedAccountOnboarded]);
  
  const onRegister = async () => {
    if (!connectedAccountId) {
      setIsConnectedAccountCreationLoading(true);
      await handleCreateConnectedAccount();
      setIsConnectedAccountCreationLoading(false);
    }
    setIsLoading(true);
    setShowOnboarding(true);
  };

  const onRegisterLater = () => {
    onClose();
  };

  const getTitle = () => {
    if (showOnboarding) {
      return 'Complete Your Stripe Registration';
    }
    if (connectedAccountId && !isConnectedAccountOnboarded) {
      return `Congratulations, and thank you for accepting ${renterName}'s Pre-application!`;
    }
    return 'Register a Stripe Account to Complete Accepting the Pre-Application!';
  };

  const getInitialContent = () => {
    if (connectedAccountId && !isConnectedAccountOnboarded) {
      return GetNotCompletedContent();
    }
    return GetStartRegistrationContent();
  };

  const GetNotCompletedContent = () => (
    <>
      <p className='mb-4'>
        Thank you for accepting the renter's pre-application.
      </p>
      <p className='mb-4'>
        To move forward, please complete your Stripe account registration — 
        our trusted third-party payment processor — before sending a lease to the renter. 
        This step is required to finalize your account setup.
      </p>
    </>
  );

  const GetStartRegistrationContent = () => (
    <>
      <p className='mb-4'>
        Congratulations, and thank you for accepting {renterName}'s pre-application!
      </p>
      <p className='mb-4'>
        To complete the next step, please set up a Stripe account, our trusted third-party payment
        processor. This is a required step to finalize your account configuration.
      </p>
    </>
  );

  return (
    <Modal
      titleClassName='text-[22px]'
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      onClose={onClose}
      isFullScreen={isMobile}
      title={getTitle()}
      contentClassName={'w-[750px]'}
    >
      {showOnboarding ? (
        <div>
          {isLoading && <Loader />}
          {stripeConnectInstance && <OnboardingComponent onLoaderStart={() => setIsLoading(false)} />}
        </div>
      ) : (
        !isConnectedAccountOnboarded && (
          <div className='text-gray-700 text-base p-5'>
            {getInitialContent()}
            <p className='mb-4'>
              Setting up Stripe will allow renters to securely pay their move-in fees online after signing the
              lease. As a landlord, you will benefit from:
            </p>
            <ul className='list-disc list-inside mb-4 space-y-1'>
              <li>Instant transfer of move-in fee payments</li>
              <li>Secure and seamless payment processing</li>
              <li>Automated tracking of incoming payments</li>
            </ul>
            <p className='mb-6'>
              Please complete your Stripe registration in the following steps to ensure a smooth leasing and payment
              experience for both you and your renters.
            </p>
            <div className='flex flex-col md:flex-row md:justify-center gap-4'>
              <Button label='Register Stripe Account' onClick={onRegister} isLoading={isConnectedAccountCreationLoading} disabled={isConnectedAccountCreationLoading} theme='primary'/>
              <Button label='Register Later' onClick={onRegisterLater} disabled={isConnectedAccountCreationLoading} theme='secondary' />
            </div>
          </div>
        )
      )}
    </Modal>
  );
};
